/*
  SIJL homepage, tweede versie - twee kleine onderdelen:
  - <sijl-lichtkrant>  de namen van de koffies lopen langzaam en gelijkmatig door het beeld, in dezelfde
                        richting als de verzendband erboven; stil als hij uit beeld is of het tabblad
                        verborgen is, met pauzeknop.
  - <sijl-smaakfilter> Alles / Fruitig / Zoet / Traditioneel in de kop van de koffierij; verbergt
                        kaarten in dezelfde sectie op basis van de smaakcollecties.
  Bij 'minder beweging' en in de thema-editor beweegt er niets.
*/

const RM = window.matchMedia('(prefers-reduced-motion: reduce)');
const ONTWERP = () => Boolean(window.Shopify && window.Shopify.designMode);

class SijlLichtkrant extends HTMLElement {
  #ac = null;
  #io = null;
  #anim = null;
  #inBeeld = false;
  #eigenPauze = false;

  connectedCallback() {
    this.#opruimen();
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    // Zet iemand 'minder beweging' aan tijdens het bezoek, dan stopt de lichtkrant meteen.
    RM.addEventListener('change', () => this.#start(), { signal });
    this.#start();
  }

  disconnectedCallback() {
    this.#opruimen();
  }

  #start() {
    this.#stop();
    const spoor = this.querySelector('.sijl-lichtkrant__spoor');
    const pauze = this.querySelector('.sijl-lichtkrant__pauze');
    if (!spoor || !pauze || RM.matches || ONTWERP()) return;

    this.classList.add('is-loopt');
    pauze.hidden = false;

    const snelheid = Number(this.dataset.snelheid || 30) * (window.innerWidth < 750 ? 0.8 : 1);
    const duur = (spoor.scrollWidth / 2 / snelheid) * 1000;
    this.#anim = spoor.animate([{ translate: '-50% 0' }, { translate: '0 0' }], {
      duration: duur,
      iterations: Infinity,
      easing: 'linear',
    });
    this.#anim.pause();

    this.#io = new IntersectionObserver((items) => {
      this.#inBeeld = items[items.length - 1].isIntersecting;
      this.#speel();
    });
    this.#io.observe(this);

    const { signal } = this.#ac;
    document.addEventListener('visibilitychange', () => this.#speel(), { signal });
    pauze.onclick = () => {
      this.#eigenPauze = pauze.getAttribute('aria-pressed') !== 'true';
      pauze.setAttribute('aria-pressed', String(this.#eigenPauze));
      this.#speel();
    };
  }

  #speel() {
    if (!this.#anim) return;
    if (this.#inBeeld && !this.#eigenPauze && !document.hidden) this.#anim.play();
    else this.#anim.pause();
  }

  #stop() {
    this.#io?.disconnect();
    this.#io = null;
    this.#anim?.cancel();
    this.#anim = null;
    this.classList.remove('is-loopt');
    const pauze = this.querySelector('.sijl-lichtkrant__pauze');
    if (pauze) pauze.hidden = true;
  }

  #opruimen() {
    this.#ac?.abort();
    this.#stop();
  }
}

class SijlSmaakfilter extends HTMLElement {
  #ac = null;

  connectedCallback() {
    this.#ac?.abort();
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const knoppen = this.querySelector('.sijl-smaakfilter__knoppen');
    const status = this.querySelector('.sijl-smaakfilter__status');
    const sectie = this.closest('.shopify-section');
    let groepen = {};
    try {
      groepen = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
    } catch (e) {
      return;
    }
    // Alleen voor een rij als raster: staan de kaarten zelf in een carrousel, dan zou het filter
    // gaten laten vallen. (Een fotoschuifje binnen een kaart telt niet.)
    const eersteVak = sectie && sectie.querySelector('.resource-list__item');
    if (!knoppen || !sectie || (eersteVak && eersteVak.closest('slideshow-component'))) return;

    const kaarten = () => {
      const gezien = new Set();
      const lijst = [];
      for (const kaart of sectie.querySelectorAll('product-card[data-product-id]')) {
        const id = Number(kaart.dataset.productId);
        if (gezien.has(id)) continue;
        gezien.add(id);
        lijst.push({ id, vak: kaart.closest('.resource-list__item') || kaart });
      }
      return lijst;
    };

    // Knoppen zonder koffies in deze rij weglaten; blijft alleen 'Alles' over, dan geen filter.
    const ids = kaarten().map((k) => k.id);
    let zinvol = 0;
    knoppen.querySelectorAll('button[data-filter]').forEach((knop) => {
      const sleutel = knop.dataset.filter;
      if (!sleutel) return;
      const n = ids.filter((id) => (groepen[sleutel] || []).includes(id)).length;
      knop.hidden = n === 0;
      if (n > 0) zinvol += 1;
    });
    if (zinvol === 0) return;
    knoppen.hidden = false;

    knoppen.addEventListener(
      'click',
      (e) => {
        const knop = e.target.closest('button[data-filter]');
        if (!knop || knop.getAttribute('aria-pressed') === 'true') return;
        const sleutel = knop.dataset.filter;
        const lijst = kaarten();

        knoppen.querySelectorAll('button').forEach((b) => b.setAttribute('aria-pressed', String(b === knop)));
        let n = 0;
        for (const { id, vak } of lijst) {
          const past = !sleutel || (groepen[sleutel] || []).includes(id);
          vak.classList.toggle('sijl-uitgefilterd', !past);
          if (past) n += 1;
        }
        const woord = n === 1 ? 'koffie' : 'koffies';
        if (status) status.textContent = sleutel ? `${knop.textContent.trim()}: ${n} ${woord}` : `Alle koffies: ${n}`;

        // Rustig: de kaarten die overblijven komen zacht in beeld, kort na elkaar.
        if (RM.matches || ONTWERP()) return;
        lijst
          .filter(({ vak }) => !vak.classList.contains('sijl-uitgefilterd'))
          .forEach(({ vak }, i) => {
            vak.animate([{ opacity: 0, translate: '0 10px' }, { opacity: 1, translate: '0 0' }], {
              duration: 320,
              delay: i * 45,
              easing: 'cubic-bezier(.16, 1, .3, 1)',
              fill: 'backwards',
            });
          });
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }
}

if (!customElements.get('sijl-lichtkrant')) customElements.define('sijl-lichtkrant', SijlLichtkrant);
if (!customElements.get('sijl-smaakfilter')) customElements.define('sijl-smaakfilter', SijlSmaakfilter);
